import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';
import { useContext, useMemo } from 'react';
import type { ContestType } from '@betterpool/api-types/contests-service';

import { UserContext } from '~/components/providers/UserProvider';
import { getMyContestsV2 } from '~/services/contests/contests-v2.service.api';
import type {
  GetMyContestsV2Params,
  GetMyContestsV2Response,
} from '~/services/contests/contests-v2.service.api';

import { ContestStatusFilter } from '../../services/enums/contest-status-filter.enum';
import isNetworkError from '../utils/isNetworkError';

const STALE_TIME = 30_000;
const PAGE_SIZE = 20;
const MAX_NETWORK_RETRIES = 2;
const NETWORK_RETRY_BASE_DELAY_MS = 500;

type InfiniteFetchMeta = { fetchMore?: { direction?: 'forward' | 'backward' } } | null;

const STATUS_FILTER_TO_V2_STATUS: Record<ContestStatusFilter, GetMyContestsV2Params['status']> = {
  [ContestStatusFilter.SCHEDULED]: 'active',
  [ContestStatusFilter.IN_PROGRESS]: 'active',
  [ContestStatusFilter.ACTION_REQUIRED]: 'action_required',
  [ContestStatusFilter.ACTIVE]: 'active',
  [ContestStatusFilter.FINALIZED]: 'finalized',
};

type UseEntriesUserV2Args = {
  status: ContestStatusFilter;
  contestType?: ContestType;
  leagues?: string[];
  hasEntryFee?: boolean;
  keepEliminatedActive?: boolean;
  sortOption?: GetMyContestsV2Params['sortOption'];
  sortDir?: GetMyContestsV2Params['sortDir'];
  limit?: number;
  enabled?: boolean;
};

const useEntriesUserV2 = ({
  status,
  contestType,
  leagues,
  hasEntryFee,
  keepEliminatedActive,
  sortOption,
  sortDir,
  limit = PAGE_SIZE,
  enabled,
}: UseEntriesUserV2Args) => {
  const { user } = useContext(UserContext);
  const userId = user?.id;
  const queryClient = useQueryClient();

  const v2Status = STATUS_FILTER_TO_V2_STATUS[status] ?? 'active';

  const hasEntryFeeParam: GetMyContestsV2Params['hasEntryFee'] = (() => {
    if (hasEntryFee === undefined) return undefined;
    return hasEntryFee ? 'true' : 'false';
  })();

  const keepEliminatedActiveKey = keepEliminatedActive === true;

  const getQueryKey = (withEliminated: boolean) =>
    [
      'contest',
      'entriesUserContestsV2',
      userId,
      status,
      contestType,
      leagues,
      limit,
      hasEntryFee,
      withEliminated,
      sortOption,
      sortDir,
    ] as const;

  const queryKey = getQueryKey(keepEliminatedActiveKey);

  const data = useInfiniteQuery({
    queryKey,
    staleTime: STALE_TIME,
    networkMode: 'always',
    retry: (failureCount, error) => isNetworkError(error) && failureCount < MAX_NETWORK_RETRIES,
    retryDelay: (attemptIndex) => NETWORK_RETRY_BASE_DELAY_MS * 2 ** attemptIndex,
    queryFn: ({ pageParam = 0 }) =>
      getMyContestsV2({
        status: v2Status,
        contestType,
        leagues: leagues?.length ? leagues.join(',') : undefined,
        hasEntryFee: hasEntryFeeParam,
        sortOption,
        sortDir,
        limit,
        offset: pageParam,
        ...(keepEliminatedActiveKey ? { keepEliminatedActive: 'true' as const } : {}),
      }),
    placeholderData: () =>
      v2Status === 'active'
        ? queryClient.getQueryData<InfiniteData<GetMyContestsV2Response>>(
            getQueryKey(!keepEliminatedActiveKey)
          )
        : undefined,
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.offset + limit;
      if (nextPage > lastPage.total) {
        return undefined;
      }
      return nextPage;
    },
    enabled: enabled && !!userId,
  });

  const itemsTotal = data.data?.pages?.[0]?.total;

  const fetchMeta: InfiniteFetchMeta | undefined = queryClient.getQueryState(queryKey)?.fetchMeta;
  const isFetchNextPageError = data.isError && fetchMeta?.fetchMore?.direction === 'forward';

  const items = useMemo(() => data.data?.pages?.flatMap((page) => page.data) ?? [], [data]);

  return {
    ...data,
    isFetchNextPageError,
    itemsTotal,
    items,
  };
};

export default useEntriesUserV2;
